import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { cn } from '../../utils/cn'

export type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text'
export type ButtonSize = 'sm' | 'md' | 'lg'

/* Stitch styles its primary action as label-caps on a solid Oroshi Blue slab
   with 32/16 padding. The smaller sizes keep the type and just tighten the box. */
const VARIANTS: Record<ButtonVariant, string> = {
  filled: 'bg-primary text-on-primary hover:opacity-90',
  tonal: 'bg-primary-fixed text-on-primary-fixed-variant hover:bg-primary-fixed-dim',
  outlined: 'border border-primary text-primary hover:bg-primary/5',
  text: 'text-primary hover:bg-primary/5',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-4 py-2',
  md: 'px-6 py-3',
  lg: 'px-8 py-4',
}

const BASE =
  'relative inline-flex items-center justify-center rounded-lg font-label-caps text-label-caps uppercase whitespace-nowrap transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-40'

interface BaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  fullWidth?: boolean
  /** Material Symbols glyph name rendered before the label. */
  icon?: string
  children?: ReactNode
  className?: string
}

type NativeButtonProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { to?: undefined }

type LinkButtonProps = BaseProps & Omit<LinkProps, keyof BaseProps> & { to: LinkProps['to'] }

export type ButtonProps = NativeButtonProps | LinkButtonProps

export function Button({
  variant = 'filled',
  size = 'md',
  loading = false,
  fullWidth,
  icon,
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className)

  const body = (
    <>
      {/* Keep the label in flow while loading so the button does not resize. */}
      <span className={cn('inline-flex items-center gap-2', loading && 'invisible')}>
        {icon && <span className="material-symbols-outlined text-[18px]">{icon}</span>}
        {children}
      </span>
      {loading && (
        <span
          aria-hidden="true"
          className="absolute h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
    </>
  )

  if (rest.to !== undefined) {
    const linkProps = rest as Omit<LinkProps, keyof BaseProps>
    return (
      <Link {...linkProps} className={classes} aria-busy={loading || undefined}>
        {body}
      </Link>
    )
  }

  // `to` is absent on this branch, but the union still carries the key — drop it
  // so it never reaches the DOM.
  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    ...(rest as Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps>),
  }
  delete (buttonProps as { to?: unknown }).to
  return (
    <button
      type="button"
      {...buttonProps}
      disabled={buttonProps.disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
    >
      {body}
    </button>
  )
}
